import { getLogger, Logger } from "./LoggingService";

interface IScreenPopConfig {
    defaultScreenPopDomain: string;
    defaultAgentSupportDomain: string;
    defaultURLTemplate: string;
    defaultWorkflowURL: string;
}

interface IAgentTransferConfig {
    preventTransferOnStatuses: string[];
}

// export interface IDispositionConfig {
//     enabled: boolean;
//     required: boolean;
//     attributeName: string;
//     defaultDisposition: string;
//     options: {
//         [name: string]: string[];
//     };
// }
export interface ICallDetailsAttribute {
    label: string;
    attributeKey: string;
    value?: string;
}

export interface ICallDetailsConfig {
    visibleAttributes: ICallDetailsAttribute[];
}

export enum URL_TYPE {
    WORKFLOW = "WORKFLOW",
    AGENT_SUPPORT = "AGENT_SUPPORT",
    SCREEN_POP = "SCREEN_POP",
}
interface IConfig {
    environment: string;
    buildTime: number;
    ccpUrl: string;
    connectUrl: string;
    ccpRegion: string;
    samlSsoUrl: string;
    logoutUrl: string;
    screenPop: IScreenPopConfig;
    agentTransferConfig: IAgentTransferConfig;
    // disposition: IDispositionConfig;
    callDetails: ICallDetailsConfig;

    /**
     * CCP Automatically calls this number to refresh the CCP Token
     */
    tokenAuthNumber: string;

    // ccpToken attribute must be set to use
    apiUrl: string;
    wssUrl: string;
}

export default class ConfigurationService implements IConfig {
    private logger: Logger;
    private cfg: IConfig = null as any;
    private popRegx: RegExp = new RegExp(/{{(\w+)}}/, "g");
    constructor() {
        this.logger = getLogger("ConfigurationService");
    }

    public async load() {
        if (!this.cfg) {
            const response = await fetch("config.json");
            this.cfg = await response.json();
            this.logger.debug("Config Loaded");
        }
    }
    get environment(): string {
        return this.cfg.environment;
    }

    get buildTime(): number {
        return this.cfg.buildTime;
    }

    get ccpUrl(): string {
        return this.cfg.ccpUrl;
    }

    get connectUrl(): string {
        const parts = this.ccpUrl.split("/");
        const hostname = parts[2];

        if (hostname.includes(".awsapps.com")) {
            return `https://${hostname}/connect`;
        } else {
            return `https://${hostname}`;
        }
    }

    get ccpRegion(): string {
        return this.cfg.ccpRegion;
    }

    get samlSsoUrl(): string {
        return this.cfg.samlSsoUrl;
    }

    get screenPop(): IScreenPopConfig {
        return this.cfg.screenPop;
    }

    get agentTransferConfig(): IAgentTransferConfig {
        return this.cfg.agentTransferConfig;
    }

    get tokenAuthNumber(): string {
        return this.cfg.tokenAuthNumber;
    }

    get apiUrl(): string {
        return this.cfg.apiUrl;
    }

    get wssUrl(): string {
        return this.cfg.wssUrl;
    }

    get logoutUrl(): string {
        return this.cfg.logoutUrl;
    }

    // get disposition(): IDispositionConfig {
    //     const notSet = {
    //         enabled: false,
    //         required: false,
    //         attributeName: "",
    //         defaultDisposition: "",
    //         options: {},
    //     };

    //     if (!this.apiUrl) {
    //         // updateAttributes is required
    //         return notSet;
    //     }
    //     return this.cfg.disposition || notSet;
    // }

    get callDetails(): ICallDetailsConfig {
        const notSet = {
            visibleAttributes: [],
        };

        if (!this.apiUrl) {
            // updateAttributes is required
            return notSet;
        }
        return this.cfg.callDetails || notSet;
    }
    getDomain(type: URL_TYPE): string {
        switch (type) {
            case URL_TYPE.SCREEN_POP:
                return this.screenPop.defaultScreenPopDomain;
            case URL_TYPE.AGENT_SUPPORT:
                return this.screenPop.defaultAgentSupportDomain;
            default:
                throw new Error("INVALID DOMAIN TYPE");
        }
    }
    getPopURLFromTemplate(template: string, type: URL_TYPE, attrs: any = {}, ani: string): string {
        template = template.replace(/\\/g, "");
        const domain = this.getDomain(type);
        const matches = template.matchAll(this.popRegx);
        return Array.from(matches).reduce((url, match) => {
            if (match[1] === "domain") {
                return url.replace(match[0], domain);
            } else if (match[1] === "incomingAni") {
                return !attrs.incomingAni ? url.replace(match[0], ani) : url.replace(match[0], attrs[match[1]]);
            } else {
                return url.replace(match[0], attrs[match[1]] ? attrs[match[1]].value : "not-set");
            }
        }, template);
    }

    getScreenPopURL(type: URL_TYPE, attrs: any, ani: string): string {
        const template =
            attrs.generateURLTemplate && attrs.generateURLTemplate.value
                ? attrs.generateURLTemplate.value
                : this.screenPop.defaultURLTemplate;
        return type === URL_TYPE.SCREEN_POP && !!attrs.screenPopURL && attrs.screenPopURL.value
            ? attrs.screenPopURL.value
            : type === URL_TYPE.AGENT_SUPPORT && !!attrs.agentSupportURL && attrs.agentSupportURL.value
                ? attrs.agentSupportURL.value
                : type === URL_TYPE.WORKFLOW
                    ? this.screenPop.defaultWorkflowURL
                    : this.getPopURLFromTemplate(template, type, attrs, ani);
    }

    getWorkflowURL(attrs: any): string {
        return !!attrs.workflowURL && attrs.workflowURL.value
            ? attrs.workflowURL.value
            : this.screenPop.defaultWorkflowURL;
    }

    isLocal(): boolean {
        return this.environment === "local";
    }

    isV2(): boolean {
        return this.ccpUrl.includes("ccp-v2");
    }
}
